import { VITE_CORE_API_URL } from "@/constants/env";
import type {
	LocationTableAction,
	LocationTableState,
} from "@/models/tables/location-table/location-table.model";
import { auth } from "@/services/auth-service";
import type { Dispatch } from "react";
import type { FilterCriteria } from "../children/SmartFilter";

// Cache for the full dataset to avoid re-fetching on pagination
// Cache duration: 5 minutes for better performance
const CACHE_DURATION_MS = 5 * 60 * 1000;

let cachedLocations: any[] | null = null;
let cacheTimestamp: number = 0;
// Map of cache keys to fetch promises - allows multiple concurrent fetches for different queries
const fetchPromises = new Map<string, Promise<any>>();

// Function to clear the location cache (useful for forcing fresh fetches after mutations)
// Call this after: creating, updating, or deleting locations
export const clearLocationCache = () => {
	cachedLocations = null;
	cacheTimestamp = 0;
	fetchPromises.clear();
};

// Helper to get nested value from an object (supports string or array paths)
const getNestedValue = (obj: any, path: string | string[]): any => {
	if (typeof path === "string") {
		return obj?.[path];
	}
	return path.reduce((acc, key) => acc?.[key], obj);
};

// Apply smart filters to the data
const applySmartFilters = (
	data: any[],
	filters: FilterCriteria[],
): any[] => {
	if (!filters || filters.length === 0) {
		return data;
	}

	return data.filter((item) => {
		// All filters must match (AND logic)
		return filters.every((filter) => {
			const itemValue = getNestedValue(item, filter.column);
			const itemValueStr = itemValue != null ? String(itemValue).toLowerCase() : "";

			if (filter.operator === "in" && Array.isArray(filter.value)) {
				// Check if item value matches any of the selected values
				return filter.value.some(
					(v) => v.toLowerCase() === itemValueStr,
				);
			}
			if (filter.operator === "equals") {
				// Exact match (case-insensitive)
				const filterValueStr = String(filter.value).toLowerCase();
				return itemValueStr === filterValueStr;
			}
			if (filter.operator === "contains") {
				// Contains match (case-insensitive)
				const filterValueStr = String(filter.value).toLowerCase();
				return itemValueStr.includes(filterValueStr);
			}
			return true;
		});
	});
};

export const fetchData = async (
	state: LocationTableState,
	dispatch: Dispatch<LocationTableAction>,
) => {
	const { pageNum, pageSize, sortBy, sortOrder, searchVal, smartFilters, viewingDeleted } = state;

	try {
		const cacheKey = viewingDeleted ? "all_locations_with_deleted" : "all_locations";
		let allLocations: any[] | undefined = undefined;

		// Check if we have valid cached data first
		if (cachedLocations && Date.now() - cacheTimestamp < CACHE_DURATION_MS) {
			allLocations = cachedLocations;
			// No loading spinner for cached data
		}

		// If no cached data, check if a fetch is already in progress
		const existingPromise = fetchPromises.get(cacheKey);
		if (!allLocations && existingPromise) {
			try {
				// Wait for the ongoing fetch to complete
				allLocations = await existingPromise;
			} catch (error) {
				// If the ongoing fetch failed, we'll try again below
				console.warn(
					"[LocationsTable] Ongoing fetch failed, will retry:",
					error,
				);
			}
		}

		// Only fetch if we don't have cached data and no fetch is in progress
		if (!allLocations && !fetchPromises.has(cacheKey)) {
			// Show loading for fresh data fetch
			dispatch({ type: "SET_IS_LOADING", payload: true });

			// Create a new fetch promise
			const newFetchPromise = (async () => {
				try {
					const url = viewingDeleted
					? `${VITE_CORE_API_URL}/core/location?include_deleted=true`
					: `${VITE_CORE_API_URL}/core/location`;
					const response = await fetch(url, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
						},
					});

					if (response.status !== 200) {
						const errorDetails = await response.json();
						throw new Error(
							`Failed to fetch locations: ${errorDetails.message || "Unknown error"}`,
						);
					}

					const responseJson = await response.json();
					const locations = responseJson.data || [];

					// Cache the fresh data
					cachedLocations = locations;
					cacheTimestamp = Date.now();

					return locations;
				} finally {
					// Clear the promise when done (success or failure)
					fetchPromises.delete(cacheKey);
				}
			})();

			// Store the promise so other calls can wait for it
			fetchPromises.set(cacheKey, newFetchPromise);

			// Wait for our own fetch to complete
			allLocations = await newFetchPromise;
		}

		// Apply client-side filtering
		let filteredLocations = allLocations || [];

		// Apply search filter
		if (searchVal?.trim()) {
			const searchLower = searchVal.toLowerCase();
			filteredLocations = filteredLocations.filter(
				(location: any) =>
					location.name?.toLowerCase().includes(searchLower) ||
					location.store_number?.toLowerCase().includes(searchLower) ||
					location.address1?.toLowerCase().includes(searchLower) ||
					location.city?.toLowerCase().includes(searchLower) ||
					location.state?.toLowerCase().includes(searchLower),
			);
		}

		// Apply smart filters (column-based advanced filters)
		if (smartFilters && smartFilters.length > 0) {
			filteredLocations = applySmartFilters(filteredLocations, smartFilters);
		}

		// Apply sorting
		if (sortBy) {
			filteredLocations.sort((a: any, b: any) => {
				const aVal = a[sortBy];
				const bVal = b[sortBy];

				// Handle null/undefined values
				if (aVal == null && bVal == null) return 0;
				if (aVal == null) return sortOrder === "desc" ? 1 : -1;
				if (bVal == null) return sortOrder === "desc" ? -1 : 1;

				// Check if both values are numbers (or can be converted to numbers)
				const aNum = Number(aVal);
				const bNum = Number(bVal);

				// If both are valid numbers, use numeric comparison
				if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
					const comparison = aNum - bNum;
					return sortOrder === "desc" ? -comparison : comparison;
				}

				// Otherwise, use string comparison
				const comparison = aVal.toString().localeCompare(bVal.toString());
				return sortOrder === "desc" ? -comparison : comparison;
			});
		}

		// Apply pagination
		const total = filteredLocations.length;
		const startIndex = (pageNum - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		const paginatedResults = filteredLocations.slice(startIndex, endIndex);

		dispatch({
			type: "SET_DATA_SOURCE",
			payload: paginatedResults,
		});
		dispatch({ type: "SET_COUNT", payload: total });
	} catch (exception) {
		console.error(`Failed to fetch locations: ${exception}`);
		dispatch({ type: "SET_DATA_SOURCE", payload: [] });
		dispatch({ type: "SET_COUNT", payload: 0 });
	} finally {
		dispatch({ type: "SET_IS_LOADING", payload: false });
	}
};
